import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TextField, Button, Typography, Divider, CircularProgress } from '@mui/material';
import { AppDispatch } from '../reduxStore/Store';
import { fetchCompteResultat, clearCompteResultat } from '../reduxStore/StatistiqueSlice';
import { CompteResultatLigne } from '../models/models';
import { useSnackbar } from './SnackbarProvider';
import AppSurface from './layout/AppSurface';
import { usePermission } from '../hooks/usePermission';
import { PERMISSION_PRIX_REVIENT } from '../constants/enums';

// 1er jour du mois courant / aujourd'hui — au format 'YYYY-MM-DD'.
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('sv-SE');
};
const today = () => new Date().toLocaleDateString('sv-SE');
const fmt = (n: number) =>
  Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Décale une date 'YYYY-MM-DD' de n années (borne les sélecteurs de date).
const addYears = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y + n, m - 1, d).toLocaleDateString('sv-SE');
};
// La période dépasse-t-elle 1 an ? (max autorisé)
const periodeTropLongue = (debut: string, fin: string) =>
  (Date.parse(fin) - Date.parse(debut)) / 86_400_000 > 366;

// Regroupe les lignes (niveau nature) par catégorie pour l'affichage à 2 niveaux.
const groupByCategorie = (lignes: CompteResultatLigne[]) => {
  const map = new Map<string, { nom: string; total: number; natures: CompteResultatLigne[] }>();
  for (const l of lignes) {
    const nom = l.categorie_nom ?? 'Sans catégorie';
    if (!map.has(nom)) map.set(nom, { nom, total: 0, natures: [] });
    const g = map.get(nom)!;
    g.total += Number(l.total_montant);
    g.natures.push(l);
  }
  return Array.from(map.values());
};

// Une ligne "libellé ........ montant".
const Ligne: React.FC<{ label: string; montant: number; bold?: boolean; indent?: number; muted?: boolean }> =
  ({ label, montant, bold, indent = 0, muted }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      sx={{ pl: indent, py: 0.4, fontWeight: bold ? 700 : 400, color: muted ? 'text.secondary' : 'text.primary' }}
    >
      <span>{label}</span>
      <span>{fmt(montant)}</span>
    </Box>
  );

/**
 * Compte de résultat — panneau embarquable (onglet de la page Stock & Statistiques).
 * Produits = ventes (CA facturé) + règlements PRODUIT ; Charges = règlements CHARGE.
 */
const CompteResultatPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { data, loading, error } = useSelector((state: any) => state.StatistiqueSlice.compteResultat);
  // Sans le droit de voir le coût (0156) : coût, marge, produits et résultat valent null
  const voitCout = usePermission(PERMISSION_PRIX_REVIENT);

  const [dateDebut, setDateDebut] = useState(firstOfMonth());
  const [dateFin, setDateFin] = useState(today());

  const charger = () => {
    if (periodeTropLongue(dateDebut, dateFin)) {
      showSnackbar('La période ne peut pas dépasser 1 an.', 'error');
      return;
    }
    dispatch(fetchCompteResultat({ date_debut: dateDebut, date_fin: dateFin }));
  };

  // Chargement initial + nettoyage du state au démontage.
  useEffect(() => {
    charger();
    return () => { dispatch(clearCompteResultat()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showSnackbar(error, 'error');
  }, [error, showSnackbar]);

  const lignes: CompteResultatLigne[] = data?.lignes ?? [];
  const produitsLignes = lignes.filter((l) => l.impact === 'PRODUIT');
  const chargesLignes  = lignes.filter((l) => l.impact === 'CHARGE');
  const neutresLignes  = lignes.filter((l) => l.impact === 'NEUTRE');

  // Affiche un groupe « catégorie + ses natures ».
  const renderGroupes = (grp: CompteResultatLigne[]) =>
    groupByCategorie(grp).map((cat) => (
      <Box key={cat.nom} sx={{ mb: 1 }}>
        <Ligne label={cat.nom} montant={cat.total} bold />
        {cat.natures.map((n, i) => (
          <Ligne
            key={n.nature_id ?? `${cat.nom}-${i}`}
            label={n.nature_nom ?? 'Sans nature'}
            montant={n.total_montant}
            indent={3}
            muted
          />
        ))}
      </Box>
    ));

  return (
    <>
      {/* ── Filtres période ── */}
      <AppSurface sx={{ mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            type="date"
            label="Du"
            value={dateDebut}
            onChange={(e) => setDateDebut(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: addYears(dateFin, -1), max: dateFin }}
          />
          <TextField
            type="date"
            label="Au"
            value={dateFin}
            onChange={(e) => setDateFin(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dateDebut, max: addYears(dateDebut, 1) }}
          />
          <Button variant="contained" onClick={charger} disabled={loading}>
            Calculer
          </Button>
        </Box>
      </AppSurface>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          {/* ── Synthèse ── */}
          <AppSurface sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Synthèse</Typography>
            <Divider sx={{ mb: 1 }} />
            {voitCout ? (
              <>
                <Ligne label="Produits" montant={data.produits} bold />
                <Ligne label="Charges" montant={data.charges} bold />
                <Divider sx={{ my: 1 }} />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ fontWeight: 800, fontSize: 18, color: data.benefice >= 0 ? 'success.main' : 'error.main' }}
                >
                  <span>{data.benefice >= 0 ? 'Bénéfice' : 'Perte'}</span>
                  <span>{fmt(data.benefice)}</span>
                </Box>
              </>
            ) : (
              <>
                <Ligne label="Charges" montant={data.charges} bold />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Marge, produits et résultat non affichés : ils dépendent du prix de revient.
                </Typography>
              </>
            )}
          </AppSurface>

          {/* ── Produits : marge sur ventes (CA − coût) + autres produits ── */}
          <AppSurface sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Produits</Typography>
            <Divider sx={{ mb: 1 }} />
            <Ligne label="Chiffre d'affaires HT (ventes)" montant={data.total_ventes} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: -0.5, mb: 0.5 }}>
              CA TTC : {fmt(data.total_ventes_ttc)}
            </Typography>
            {voitCout && (
              <>
                <Ligne label="Coût des marchandises vendues (HT)" montant={-data.total_cout} muted />
                <Ligne label="Marge brute" montant={data.marge} bold />
              </>
            )}
            {produitsLignes.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Autres produits</Typography>
                {renderGroupes(produitsLignes)}
              </Box>
            )}
            {voitCout && (
              <>
                <Divider sx={{ my: 1 }} />
                <Ligne label="Total produits" montant={data.produits} bold />
              </>
            )}
          </AppSurface>

          {/* ── Charges ── */}
          <AppSurface sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Charges</Typography>
            <Divider sx={{ mb: 1 }} />
            {chargesLignes.length === 0 && (data.dotation_amortissements ?? 0) === 0
              ? <Typography variant="body2" color="text.secondary">Aucune charge sur la période.</Typography>
              : (
                <>
                  {renderGroupes(chargesLignes)}
                  {(data.dotation_amortissements ?? 0) > 0 && (
                    <Ligne label="Dotation aux amortissements" montant={data.dotation_amortissements} />
                  )}
                </>
              )}
            <Divider sx={{ my: 1 }} />
            <Ligne label="Total charges" montant={data.charges} bold />
          </AppSurface>

          {/* ── Mouvements neutres (exclus du calcul) ── */}
          {neutresLignes.length > 0 && (
            <AppSurface>
              <Typography variant="h6" gutterBottom>Mouvements neutres</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Exclus du calcul du résultat (virements internes, apports, etc.)
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {renderGroupes(neutresLignes)}
              <Divider sx={{ my: 1 }} />
              <Ligne label="Total neutres" montant={data.neutres} bold />
            </AppSurface>
          )}
        </>
      )}
    </>
  );
};

export default CompteResultatPanel;
